import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import DemoFormDialog from "@/components/DemoFormDialog";
import FaqAccordion from "@/components/FaqAccordion";
import FinalCta from "@/components/FinalCta";
import { PRICING, FAQS } from "@/data/site";
import { useSeo } from "@/lib/seo";
import Breadcrumbs from "@/components/Breadcrumbs";

const MESSAGE_TYPES = [
  { name: "Marketing", free: false, desc: "Promotions, offers, product launches and re-engagement messages.", eg: "Festive sale announcement" },
  { name: "Utility", free: false, desc: "Updates about an existing order, booking or account.", eg: "Order shipped, appointment reminder" },
  { name: "Authentication", free: false, desc: "One-time passwords and login verification codes.", eg: "Your OTP is 482913" },
  { name: "Service", free: true, desc: "Your replies to customers within the 24-hour customer service window.", eg: "Answering a customer's question" },
];

const MATRIX = [
  { feature: "Conversations per month", values: ["1,000", "10,000", "Unlimited"] },
  { feature: "Team members", values: ["2", "10", "Unlimited"] },
  { feature: "No-Code Chatbot Builder", values: [true, true, true] },
  { feature: "Shared Team Inbox", values: [false, true, true] },
  { feature: "Bulk Broadcasts", values: [true, true, true] },
  { feature: "Automation Journeys", values: ["Basic", "Advanced", "Advanced + A/B"] },
  { feature: "Built-in CRM", values: [false, true, true] },
  { feature: "Custom Integrations & API", values: [false, true, true] },
  { feature: "Green Tick Assistance", values: [false, "On request", "Dedicated"] },
  { feature: "SSO + Audit logs", values: [false, false, true] },
  { feature: "Dedicated Account Manager", values: [false, false, true] },
  { feature: "Support", values: ["Email", "Priority chat", "Phone + Slack + SLA"] },
];

export default function PricingPage() {
  useSeo({
    title: "WhatsApp Business API Pricing & Plans | GrabNChat",
    description: "GrabNChat pricing: Starter, Growth and Enterprise plans for WhatsApp marketing, chatbots, CRM and team inbox, plus how Meta's per-message WhatsApp charges work.",
  });

  const renderCell = (v) => {
    if (v === true) return <CheckCircle2 className="w-5 h-5 text-[#25D366] mx-auto" />;
    if (v === false) return <XCircle className="w-5 h-5 text-slate-300 mx-auto" />;
    return <span className="text-sm font-medium text-slate-900">{v}</span>;
  };

  return (
    <div data-testid="pricing-page">
      <section className="pt-8 md:pt-10 pb-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><Breadcrumbs items={[{ name: "Pricing", path: "/pricing" }]} /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-12 md:pt-16">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
            WhatsApp Business API Pricing that scales with you
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">7-day free trial on every plan. Cancel anytime. No setup fees.</p>
        </div>
      </section>

      <section className="pb-20">
        <h2 className="sr-only">GrabNChat plans</h2>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6">
          {PRICING.map((p, i) => (
            <div key={i} className={`relative rounded-3xl border p-8 ${p.popular ? "border-[#25D366] bg-white shadow-2xl" : "border-slate-200 bg-white"}`} data-testid={`plan-card-${p.name.toLowerCase()}`}>
              {p.popular && <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[#25D366] text-white text-xs font-semibold">Most popular</div>}
              <h3 className="font-display text-2xl font-bold text-slate-900">{p.name}</h3>
              <p className="text-sm text-slate-500 mt-1">{p.desc}</p>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-display text-5xl font-bold text-slate-900">{p.price}</span>
                <span className="text-slate-500">{p.period}</span>
              </div>
              <ul className="mt-7 space-y-3">
                {p.features.map((f, k) => (
                  <li key={k} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-[#25D366] mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <div className="mt-8">
                <DemoFormDialog source={p.name.toLowerCase()} defaultMessage={`I'm interested in the ${p.name} plan.`} trigger={
                  <Button className={`w-full rounded-full ${p.popular ? "bg-[#25D366] hover:bg-[#1DA851]" : "bg-[#0F172A] hover:bg-[#1E293B]"} text-white font-semibold h-11`} data-testid={`plan-cta-${p.name.toLowerCase()}`}>
                    {p.cta} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                } />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-20" data-testid="pricing-matrix">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 text-center">Compare plans</h2>
          <div className="mt-10 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left p-4 text-sm font-semibold text-slate-900">Features</th>
                  {PRICING.map((p) => (
                    <th key={p.name} className="p-4 text-sm font-semibold text-slate-900 text-center">{p.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MATRIX.map((row, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="p-4 text-sm text-slate-700">{row.feature}</td>
                    {row.values.map((v, k) => (
                      <td key={k} className="p-4 text-center">{renderCell(v)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-slate-50" data-testid="message-charges">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-widest text-[#25D366] font-semibold mb-3">WhatsApp message charges</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">How WhatsApp message pricing works</h2>
            <p className="mt-4 text-lg text-slate-600">Your plan covers the GrabNChat platform. Meta separately charges for template messages by category; these are passed on at cost and shown live in your dashboard wallet.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {MESSAGE_TYPES.map((m) => (
              <div key={m.name} className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display text-lg font-semibold text-slate-900">{m.name}</h3>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${m.free ? "bg-[#25D366]/10 text-[#1DA851]" : "bg-slate-100 text-slate-700"}`}>{m.free ? "Free" : "Per message"}</span>
                </div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{m.desc}</p>
                <p className="mt-3 text-xs text-slate-500">e.g. {m.eg}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-700 leading-relaxed">
            <strong className="text-slate-900">The 24-hour customer service window:</strong> when a customer messages you, a 24-hour window opens in which your free-form replies are not charged by Meta. Outside that window you need an approved template. Meta's rates depend on the category and the customer's country; see Meta's official WhatsApp pricing for the current rates.
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 ">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 text-center">Pricing FAQs</h2>
          <div className="mt-10"><FaqAccordion faqs={FAQS.slice(0, 8)} /></div>
        </div>
      </section>

      <FinalCta title="Pick a plan and go live in 24 hours" sub="Try GrabNChat free for 7 days. We'll help you set up." />
    </div>
  );
}
